import { useEffect, useMemo, useState } from "react";

import { ApiError, fetchFleetSummary, getApiBaseUrl } from "./lib/api";
import BuildInfoFooter from "./components/BuildInfoFooter";
import { CONTROL_CATALOG_V1 } from "./lib/controlCatalog";
import { SAMPLE_EVIDENCE_MAPPING_INDEX } from "./lib/evidenceMapping";

interface FleetSummary {
  onlineAgents: number;
  offlineAgents: number;
  lastUpdated?: string;
  _demo?: boolean;
}

type FleetSummaryState =
  | { status: "loading" }
  | { status: "success"; data: FleetSummary; isDemo: boolean }
  | { status: "error"; error: ApiError };

function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof Error) return new ApiError(error.message);
  return new ApiError(String(error));
}

function useFleetSummary(apiBaseUrl: string): {
  state: FleetSummaryState;
  retry: () => void;
} {
  const [state, setState] = useState<FleetSummaryState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  function retry(): void {
    setAttempt((prev) => prev + 1);
  }

  useEffect(() => {
    let cancelled = false;

    async function loadSummary(): Promise<void> {
      try {
        setState({ status: "loading" });
        const data = await fetchFleetSummary(apiBaseUrl);
        if (!cancelled) {
          setState({ status: "success", data, isDemo: Boolean(data._demo) });
        }
      } catch (error) {
        const apiError = toApiError(error);
        console.warn("Failed to load fleet summary", apiError);
        if (!cancelled) {
          setState({ status: "error", error: apiError });
        }
      }
    }

    void loadSummary();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, attempt]);

  return { state, retry };
}

export default function App(): JSX.Element {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { state: fleetSummaryState, retry: retryFleetSummary } = useFleetSummary(apiBaseUrl);
  const [activeView, setActiveView] = useState<"overview" | "controls" | "evidence">("overview");

  useEffect(() => {
    document.title = "Verity Edge — Operations Dashboard";
  }, []);

  return (
    <div style={{ margin: "0 auto", maxWidth: "var(--container)", padding: "var(--gutter)" }}>
      <header
        style={{
          borderBottom: "1px solid var(--border)",
          paddingBottom: "1rem",
          marginBottom: "1.5rem",
          background: "linear-gradient(180deg, var(--surface-soft), rgba(255,255,255,0.95))",
          borderRadius: "var(--radius)",
          padding: "1rem 1rem 1.25rem"
        }}
      >
        <div style={{
          background: "linear-gradient(90deg, var(--brand), var(--brand-d))",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          fontWeight: 800,
        }}>
          <h1 style={{ margin: 0 }}>Verity Edge — Operations Dashboard</h1>
        </div>
        <p style={{ color: "var(--muted)", marginTop: ".5rem" }}>API endpoint: {apiBaseUrl}</p>

        <nav aria-label="Dashboard views" style={{ display: "flex", gap: ".5rem", marginTop: "1rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setActiveView("overview")}
            style={{
              background: activeView === "overview" ? "var(--brand)" : "var(--surface)",
              color: activeView === "overview" ? "white" : "var(--brand)",
              border: "1px solid var(--brand)",
              borderRadius: "999px",
              padding: ".4rem .75rem",
              cursor: "pointer"
            }}
          >
            Overview
          </button>
          <button
            type="button"
            onClick={() => setActiveView("controls")}
            style={{
              background: activeView === "controls" ? "var(--brand)" : "var(--surface)",
              color: activeView === "controls" ? "white" : "var(--brand)",
              border: "1px solid var(--brand)",
              borderRadius: "999px",
              padding: ".4rem .75rem",
              cursor: "pointer"
            }}
          >
            Controls
          </button>
          <button
            type="button"
            onClick={() => setActiveView("evidence")}
            style={{
              background: activeView === "evidence" ? "var(--brand)" : "var(--surface)",
              color: activeView === "evidence" ? "white" : "var(--brand)",
              border: "1px solid var(--brand)",
              borderRadius: "999px",
              padding: ".4rem .75rem",
              cursor: "pointer"
            }}
          >
            Evidence
          </button>
        </nav>
      </header>

      {activeView === "overview" ? (
        <>
          <section>
            <h2 style={{ marginTop: 0 }}>Fleet Overview</h2>
            {fleetSummaryState.status === "success" ? (
              <>
                {fleetSummaryState.isDemo ? (
                  <div
                    role="status"
                    style={{
                      background: "var(--warning-bg)",
                      border: "1px solid var(--warning-border)",
                      color: "var(--warning-text)",
                      padding: ".75rem",
                      borderRadius: "8px",
                      marginBottom: "1rem"
                    }}
                  >
                    Demo mode: fleet metrics are simulated (no operator JWT found).
                  </div>
                ) : null}
                <ul>
                  <li>Online agents: {fleetSummaryState.data.onlineAgents}</li>
                  <li>Offline agents: {fleetSummaryState.data.offlineAgents}</li>
                </ul>
                {fleetSummaryState.data.lastUpdated ? (
                  <p style={{ color: "var(--muted)", marginTop: ".5rem" }}>Last updated: {fleetSummaryState.data.lastUpdated}</p>
                ) : null}
              </>
            ) : null}

            {fleetSummaryState.status === "loading" ? <p>Loading fleet metrics…</p> : null}

            {fleetSummaryState.status === "error" ? (
              <div
                role="alert"
                style={{
                  background: "var(--danger-bg)",
                  border: "1px solid var(--danger-border)",
                  color: "var(--danger-text)",
                  padding: ".75rem",
                  borderRadius: "8px"
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: ".25rem" }}>Could not load fleet metrics</div>
                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
                  {fleetSummaryState.error.status ? `HTTP ${fleetSummaryState.error.status} — ` : ""}
                  {fleetSummaryState.error.message}
                </div>
                {fleetSummaryState.error.url ? (
                  <div style={{ marginTop: ".25rem", color: "var(--danger-text-strong)" }}>{fleetSummaryState.error.url}</div>
                ) : null}
                <button
                  type="button"
                  onClick={retryFleetSummary}
                  style={{
                    marginTop: ".75rem",
                    background: "var(--danger-border)",
                    color: "white",
                    border: "1px solid var(--danger-border)",
                    borderRadius: "8px",
                    padding: ".5rem .75rem",
                    cursor: "pointer"
                  }}
                >
                  Retry
                </button>
              </div>
            ) : null}
            <div style={{ display: "flex", gap: ".5rem", marginTop: "1rem" }}>
              <button
                type="button"
                style={{
                  background: "var(--brand)",
                  color: "white",
                  border: "1px solid var(--brand)",
                  borderRadius: "8px",
                  padding: ".5rem .75rem",
                  cursor: "pointer"
                }}
              >
                Download PDF report
              </button>
              <button
                type="button"
                style={{
                  background: "var(--surface)",
                  color: "var(--brand)",
                  border: "1px solid var(--brand)",
                  borderRadius: "8px",
                  padding: ".5rem .75rem",
                  cursor: "pointer"
                }}
              >
                Download CSV report
              </button>
            </div>
          </section>

          <section>
            <h2>Launch Checklist</h2>
            <ol>
              <li>Ensure identity providers issue JWTs for operators and admins.</li>
              <li>Confirm Cloud Run and Cloudflare deployments completed via GitHub Actions.</li>
              <li>Verify offline queue processing and audit logs in the backend.</li>
            </ol>
          </section>
        </>
      ) : null}

      {activeView === "controls" ? (
        <section>
          <h2 style={{ marginTop: 0 }}>Control Catalog (v1)</h2>
          <p style={{ color: "var(--muted)" }}>
            Controls are tracked as VE-Cxx IDs. Framework mappings (including NIS2) are optional outputs generated from the
            same evidence.
          </p>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: ".5rem", borderBottom: "1px solid var(--border)" }}>ID</th>
                  <th style={{ textAlign: "left", padding: ".5rem", borderBottom: "1px solid var(--border)" }}>Control</th>
                  <th style={{ textAlign: "left", padding: ".5rem", borderBottom: "1px solid var(--border)" }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {CONTROL_CATALOG_V1.map((control) => (
                  <tr key={control.id}>
                    <td style={{ padding: ".5rem", borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap" }}>{control.id}</td>
                    <td style={{ padding: ".5rem", borderBottom: "1px solid var(--border-subtle)" }}>{control.name}</td>
                    <td style={{ padding: ".5rem", borderBottom: "1px solid var(--border-subtle)" }}>{control.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeView === "evidence" ? (
        <section>
          <h2 style={{ marginTop: 0 }}>Evidence</h2>
          <p style={{ color: "var(--muted)" }}>
            Evidence is exported as time-scoped, auditor-friendly packs with reproducible hashes and a control mapping
            index.
          </p>

          <h3 style={{ marginBottom: ".5rem" }}>Sample evidence mapping</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: ".5rem", borderBottom: "1px solid var(--border)" }}>Control</th>
                  <th style={{ textAlign: "left", padding: ".5rem", borderBottom: "1px solid var(--border)" }}>Evidence file</th>
                  <th style={{ textAlign: "left", padding: ".5rem", borderBottom: "1px solid var(--border)" }}>Type</th>
                  <th style={{ textAlign: "left", padding: ".5rem", borderBottom: "1px solid var(--border)" }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {SAMPLE_EVIDENCE_MAPPING_INDEX.map((row, idx) => (
                  <tr key={`${row.controlId}:${row.evidencePath}:${idx}`}>
                    <td style={{ padding: ".5rem", borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap" }}>{row.controlId}</td>
                    <td style={{ padding: ".5rem", borderBottom: "1px solid var(--border-subtle)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
                      {row.evidencePath}
                    </td>
                    <td style={{ padding: ".5rem", borderBottom: "1px solid var(--border-subtle)" }}>{row.evidenceType}</td>
                    <td style={{ padding: ".5rem", borderBottom: "1px solid var(--border-subtle)" }}>{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ marginBottom: ".5rem", marginTop: "1.25rem" }}>Verifier workflow (example)</h3>
          <ol>
            <li>Verify the ZIP checksum using the published SHA256 file.</li>
            <li>
              Verify internal file hashes using <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>checksums.sha256</span>.
            </li>
            <li>Use the control mapping index to trace each VE-Cxx control to its supporting artifacts.</li>
          </ol>
        </section>
      ) : null}

      <BuildInfoFooter apiBaseUrl={apiBaseUrl} />
    </div>
  );
}
